import { ReportEntry } from '@mbrtargeting/metatag-shared-types';
import { isPromiseLike } from '@mbrtargeting/metatag-utils';

export class ReportEntriesStore {

    private db: IDBDatabase | null = null;
    private openPromise: Promise<void> | null = null;

    constructor(
        private readonly dbName: string = 'SDG',
        private readonly storeName: string = 'report-entries',
    ) { }

    /**
     * Opens the IndexedDB database and creates the object store if needed.
     * Safe to call multiple times - returns the same promise if already opening.
     */
    async open(): Promise<this> {
        if (this.db) return this;

        this.openPromise ||= new Promise<void>((resolve, reject) => {
            const request = indexedDB.open(this.dbName, 1);

            request.onerror = () => {
                this.openPromise = null;
                reject(new Error(`Failed to open: ${request.error?.message}`));
            };

            request.onupgradeneeded = () => {
                const db = request.result;
                if (!db.objectStoreNames.contains(this.storeName)) {
                    const store = db.createObjectStore(this.storeName, {
                        autoIncrement: true,
                        keyPath: 'id'
                    });
                    store.createIndex('date', 'date', { unique: false });
                }
            };

            request.onsuccess = () => {
                this.db = request.result;
                this.openPromise = null;
                resolve();
            };
        });

        await this.openPromise;
        return this;
    }

    /**
     * Adds a report entry to the IndexedDB store.
     */
    async add(entry: ReportEntry): Promise<IDBValidKey> {
        return this.#executeTransaction('readwrite', store => store.add(entry));
    }

    /**
     * Deletes entries from store that are older than the specified number of minutes.
     */
    async truncate(minutes: number): Promise<void> {
        const cutoffDate = new Date(Date.now() - minutes * 6e4);

        return this.#executeTransaction('readwrite', store => {
            return new Promise<void>((resolve, reject) => {
                const index = store.index('date');
                const request = index.openCursor(IDBKeyRange.upperBound(cutoffDate));

                request.onsuccess = (event) => {
                    const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
                    if (!cursor) {
                        resolve();
                        return;
                    }
                    cursor.delete();
                    cursor.continue();
                };

                request.onerror = () => reject(request.error);
            });
        });
    }

    /**
     * Helper method to execute in a transaction with proper error handling.
     */
    async #executeTransaction<T>(mode: IDBTransactionMode, callback: (store: IDBObjectStore) => IDBRequest<T> | Promise<T>): Promise<T> {
        return new Promise<T>((resolve, reject) => {
            try {
                if (!this.db) throw new Error('Database not opened.');
                const transaction = this.db.transaction(this.storeName, mode, { durability: 'relaxed' });
                const store = transaction.objectStore(this.storeName);

                const result = callback(store);

                if (isPromiseLike(result)) {
                    result.then(resolve).catch(reject);
                } else {
                    result.onsuccess = () => resolve(result.result);
                    result.onerror = () => reject(result.error);
                }
            } catch (error) {
                reject(error);
            }
        });
    }
}
